import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import t from "../../../i18n/i18n";
import initPage from "../../../utils/initPage";
import useApi from "../../../services/api";
import { AddToCard, ClearCart } from "../../../store/reducers/cart.reducer";

import Template from "../../../template/template";
import UserBox from "../userBox/userBox";
import PaymentBox from "../paymentBox/paymentBox";
import Servers from "../../../components/servers/servers";
import { handleApiErrors } from "../../../utils/handleErrors";

import "./styles.css";

function RechargeCupons() {
  initPage({ title: t("RechargeCenter.Cupons.Title") });

  const {
    user: { loyal_member_expiration, loyal_member_points: lmp },
  } = useSelector((store) => store.auth);
  const loyalMemberActive = useMemo(() => {
    return new Date() < new Date(loyal_member_expiration);
  }, [loyal_member_expiration]);
  const [loadingCharacters, setLoadingCaracters] = useState(false);

  const { ApiGetCharacters, ApiGetCupons, ApiGetLoyalMember } = useApi();

  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [characters, setCharacters] = useState([]);
  const [cupons, setCupons] = useState([]);
  const [currentLoyalMember, setCurrentLoyalMember] = useState({});

  useEffect(() => {
    const getCurrentLoyalMember = async () => {
      if (!loyalMemberActive) return;

      const { data } = await ApiGetLoyalMember();

      if (data.errors) {
        return handleApiErrors(data.errors);
      }

      data.sort((a, b) => {
        if (a.target_points < b.target_points) return -1;
        if (a.target_points > b.target_points) return 1;
        return 0;
      });

      for (let i = 0; i < data.length; i++) {
        if (lmp >= data[i].target_points && (data[i + 1] ? lmp < data[i + 1].target_points : true)) {
          setCurrentLoyalMember(data[i]);
        }
      }
    };

    getCurrentLoyalMember();
  }, [ApiGetLoyalMember, lmp, loyalMemberActive]);

  async function getCupons() {
    const { data } = await ApiGetCupons();

    data.sort((a, b) => {
      if (a.cupons < b.cupons) return -1;
      if (a.cupons > b.cupons) return 1;
      return 0;
    });

    setCupons(data);
  }

  useEffect(() => {
    getCupons();

    return () => {
      dispatch(ClearCart());
    };
  }, []);

  async function onSelectServer(e) {
    const server = e.target.value;
    if (server === form.server) return;

    setLoadingCaracters(true);
    setForm({ ...form, server });

    const { data } = await ApiGetCharacters(server);

    setCharacters(data);
    setLoadingCaracters(false);
  }

  function onSelectCharacter(e) {
    setForm({ ...form, character: e.target.value });
  }

  function handleSelectRadio(e) {
    const payload = {
      count: 1,
      cupom: cupons.find((c) => c.id === parseInt(e.target.value, 10)),
      form,
    };

    dispatch(AddToCard(payload));
  }

  function handleLMPrice(price) {
    switch (currentLoyalMember.name) {
      case "Silver": {
        return (price *= 0.97);
      }
      case "Gold": {
        return (price *= 0.96);
      }
      case "Platinum": {
        return (price *= 0.96);
      }
      case "Diamond": {
        return (price *= 0.95);
      }
      default: {
        return price;
      }
    }
  }

  return (
    <Template>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="chargin-warp-main">
              <div className="chargin-col-left">
                <UserBox />

                <div className="chargin-box-panel">
                  <Servers onSelect={onSelectServer} />
                </div>

                {form?.server && (
                  <div className="chargin-box-panel">
                    <div className="select-character">
                      <div className="input-custom-select">
                        <span className="step-counter">2</span>
                        <select onChange={onSelectCharacter} disabled={loadingCharacters}>
                          <option value="" disabled selected>
                            {loadingCharacters ? t("General.Loading") : t("General.SelectCharacter")}
                          </option>
                          {characters.map((C) => {
                            return <option value={C.UserID}>{C.NickName}</option>;
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {form?.server && form?.character && (
                  <>
                    <div className="chargin-box-panel">
                      <div className="select-package">
                        <h3>
                          <span className="step-counter">3</span> {t("RechargeCenter.Cupons.SelectPack")}
                        </h3>

                        <ul className="package-list">
                          {cupons.map((cupom) => {
                            return (
                              <li>
                                <div className="package-box">
                                  <div className="link-cover">
                                    <input
                                      type="radio"
                                      value={cupom.id}
                                      name="pack"
                                      id={`pack${cupom.id}`}
                                      onClick={handleSelectRadio}
                                    />
                                    <label for={`pack${cupom.id}`}>
                                      <div className="price-info">
                                        <div className="price">
                                          {t("RechargeCenter.CurrencySymbol")}
                                          {handleLMPrice(cupom.price).toFixed(2)}
                                          {cupom.credits > 0 && (
                                            <small className="price-credits">
                                              {t("RechargeCenter.Credit.Credits", { credits: cupom.credits })}
                                            </small>
                                          )}
                                        </div>
                                        <div className="price-gold">
                                          <img src={`/images/${cupom.icon}`} alt="" />
                                        </div>
                                      </div>
                                      <div className="cupon-info">
                                        <h6>
                                          {t("RechargeCenter.Cupons.Cupons", { cupons: cupom.cupons })}
                                          <br />+ {t("RechargeCenter.Cupons.Extra", { extra: cupom.extra })}
                                        </h6>
                                      </div>
                                      {cupom.discount && <span className="discount">{t("General.Discount")}</span>}
                                    </label>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    <PaymentBox step="4" credit />
                  </>
                )}
              </div>
              <div className="chargin-col-right">
                <div className="chargin-banner-box">
                  <a href="#">
                    <img src={t("Images.A001")} className="view-large" />
                    <img src={t("Images.A002")} className="view-small" />
                  </a>
                </div>
                <div className="chargin-banner-box">
                  <a href="#">
                    <img src={t("Images.A003")} className="view-large" />
                    <img src={t("Images.A004")} className="view-small" />
                  </a>
                </div>
                <div className="chargin-banner-box">
                  <a href="#">
                    <img src={t("Images.A005")} className="view-large" />
                    <img src={t("Images.A006")} className="view-small" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Template>
  );
}

export default RechargeCupons;
